import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, GoldButton } from "@/admin/ui";

export const ImagesManager = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/images").then(({ data }) => setImages(data));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Upload concluído (compressão automática)");
      await load();
    } catch { toast.error("Falha no upload"); }
    finally { setUploading(false); }
  };

  const remove = async (img) => {
    if (!window.confirm("Excluir imagem?")) return;
    await api.delete(`/admin/images/${img.id}`);
    toast.success("Imagem excluída");
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div data-testid="admin-images">
      <PageTitle title="Gerenciador de Imagens" subtitle={`${images.length} imagens • compressão automática`}
        action={
          <GoldButton onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="image-upload-btn">
            <Upload className="w-4 h-4 inline mr-1" /> {uploading ? "Enviando..." : "Upload"}
          </GoldButton>
        } />
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files))} data-testid="image-file-input" />

      {images.length === 0 ? (
        <Card className="text-center py-16 text-white/40">Nenhuma imagem enviada ainda.</Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((img) => {
            const url = `/api/images/${img.id}`;
            return (
              <Card key={img.id} className="!p-2 group">
                <div className="aspect-square rounded-lg overflow-hidden bg-white/[0.02]">
                  <img src={imgUrl(url)} alt={img.filename} className="w-full h-full object-cover" />
                </div>
                <div className="text-[11px] text-white/50 truncate mt-2 px-1">{img.filename}</div>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => copy(url)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white/[0.04] text-white/70 hover:text-[#F5C542] text-xs">
                    {copied === url ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copiar
                  </button>
                  <button onClick={() => remove(img)} className="px-2 py-1.5 rounded-lg bg-white/[0.04] text-white/70 hover:text-[#FF3B30]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
